/**
 * Shared catalog period-discount helpers (mirrors API productDiscountResolver).
 */

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

/**
 * Optional cap on the discount amount (e.g. 10% but never over €250).
 */
export const getDiscountMaxAmount = (discount) => {
  const raw = discount?.max_amount ?? discount?.maxAmount;
  if (raw === null || raw === undefined || raw === '') return null;
  const max = Number(raw);
  return Number.isFinite(max) && max > 0 ? max : null;
};

export const applyDiscountToAmount = (totalPrice, discount) => {
  const total = Math.max(0, Number(totalPrice) || 0);
  if (!discount || !discount.type) {
    return { amount: 0, discountedTotal: round2(total) };
  }

  const value = Math.max(0, Number(discount.value) || 0);
  let amount = 0;

  if (discount.type === 'percentage') {
    amount = (total * Math.min(value, 100)) / 100;
  } else if (discount.type === 'fixed') {
    amount = value;
  }

  const maxAmount = getDiscountMaxAmount(discount);
  if (maxAmount != null) {
    amount = Math.min(amount, maxAmount);
  }

  amount = round2(Math.min(amount, total));
  return {
    amount,
    discountedTotal: round2(Math.max(0, total - amount)),
  };
};

export const getProductActiveDiscount = (product) =>
  product?.activeDiscount || product?.active_discount || null;

const formatEuro = (value, locale = 'nl-NL') => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch {
    return `€${round2(value)}`;
  }
};

export const formatDiscountBadgeLabel = (discount, locale = 'nl-NL') => {
  if (!discount) return '';
  const value = Number(discount.value) || 0;
  if (discount.type === 'percentage') {
    const label = `-${value % 1 === 0 ? value : round2(value)}%`;
    const maxAmount = getDiscountMaxAmount(discount);
    return maxAmount != null ? `${label} (max ${formatEuro(maxAmount, locale)})` : label;
  }
  return `-${formatEuro(value, locale)}`;
};

export const shouldShowDiscountBadge = (discount) => {
  if (!discount) return false;
  return discount.show_badge !== false && discount.showBadge !== false;
};

export const buildCatalogDiscountSnapshot = (discount, amount) => {
  if (!discount) return null;
  const snapshot = {
    id: discount.id,
    title: discount.title,
    type: discount.type,
    value: Number(discount.value),
    amount: round2(amount),
    method: discount.method || 'automatic',
  };
  if (discount.code) snapshot.code = discount.code;
  const maxAmount = getDiscountMaxAmount(discount);
  if (maxAmount != null) snapshot.max_amount = maxAmount;
  return snapshot;
};

/**
 * Apply catalog discount to a CPQ pricing result (mutates a shallow copy).
 */
export const applyCatalogDiscountToPricingResult = (result, discount) => {
  if (!result || !discount) return result;

  const originalTotal = Number(result.totalPrice) || 0;
  const originalProductOnly =
    typeof result.productOnlyPrice === 'number'
      ? result.productOnlyPrice
      : originalTotal - (Number(result.upsellsPrice) || 0);
  const originalUpsells = Number(result.upsellsPrice) || 0;

  const { amount, discountedTotal } = applyDiscountToAmount(originalTotal, discount);
  if (amount <= 0) {
    return {
      ...result,
      originalTotalPrice: originalTotal,
      catalogDiscountAmount: 0,
      catalogDiscount: null,
    };
  }

  const ratio = originalTotal > 0 ? discountedTotal / originalTotal : 1;
  const productOnlyPrice = round2(originalProductOnly * ratio);
  const upsellsPrice = round2(discountedTotal - productOnlyPrice);

  const snapshot = buildCatalogDiscountSnapshot(discount, amount);
  const breakdown = Array.isArray(result.breakdown) ? [...result.breakdown] : [];
  breakdown.push({
    type: 'catalog_discount',
    label: discount.title || 'Korting',
    value: -amount,
    price: -amount,
    quantity: 1,
    discount: snapshot,
  });

  return {
    ...result,
    totalPrice: discountedTotal,
    originalTotalPrice: originalTotal,
    productOnlyPrice,
    upsellsPrice,
    catalogDiscountAmount: amount,
    catalogDiscount: snapshot,
    breakdown,
    hiddenTotal: discountedTotal,
  };
};

const targetIds = (discount, key) => {
  const targets = Array.isArray(discount?.targets) ? discount.targets : [];
  const camelKey = key === 'product_id' ? 'productId' : 'categoryId';
  const fromTargets = targets.map((target) => target?.[key] || target?.[camelKey]);
  const explicit =
    key === 'product_id'
      ? discount?.product_ids || discount?.productIds || []
      : discount?.category_ids || discount?.categoryIds || [];
  return [...fromTargets, ...explicit].filter(Boolean).map(String);
};

const categoryIdsOf = (product = {}) => {
  const ids = new Set();
  (product.categoryIds || product.category_ids || []).forEach((id) => id && ids.add(String(id)));
  (product.categories || []).forEach((category) => ids.add(String(category?.id || category)));
  return [...ids];
};

const codeMatchesProduct = (discount, product) => {
  if (!discount || !product) return false;
  const scope = discount.scope || 'all';
  if (scope === 'all') return true;
  const productId = String(product.id || product.productId || '');
  if (scope === 'products') return targetIds(discount, 'product_id').includes(productId);
  if (scope === 'categories') {
    const ids = categoryIdsOf(product);
    const targets = new Set(targetIds(discount, 'category_id'));
    return ids.some((id) => targets.has(id));
  }
  return false;
};

/**
 * Per line, keep the automatic discount unless the redeemed code is worth more.
 * A fixed code is one amount across the matching lines.
 */
export const allocateDiscountCode = (lines, codeDiscount) => {
  const list = Array.isArray(lines) ? lines : [];
  const automaticLine = (line) => ({
    key: line.key,
    source: 'automatic',
    unitAmount: line.automaticUnitAmount,
    unitTotal: round2(Math.max(0, line.unitBase - line.automaticUnitAmount)),
  });

  const decorated = list.map((line, index) => ({
    key: line?.key != null ? line.key : index,
    product: line?.product || line,
    unitBase: Math.max(0, Number(line?.unitBase) || 0),
    quantity: Math.max(1, Number(line?.quantity) || 1),
    automaticUnitAmount: Math.max(0, Number(line?.automaticUnitAmount) || 0),
    matches: codeMatchesProduct(codeDiscount, line?.product || line),
  }));

  if (!codeDiscount || !codeDiscount.type) {
    return { lines: decorated.map(automaticLine), codeAmount: 0, applied: false };
  }

  if (codeDiscount.type === 'percentage') {
    let codeAmount = 0;
    let applied = false;
    const out = decorated.map((line) => {
      if (!line.matches) return automaticLine(line);
      const codeUnit = applyDiscountToAmount(line.unitBase, codeDiscount).amount;
      if (codeUnit <= line.automaticUnitAmount + 0.001) return automaticLine(line);
      applied = true;
      codeAmount += codeUnit * line.quantity;
      return {
        key: line.key,
        source: 'code',
        unitAmount: codeUnit,
        unitTotal: round2(Math.max(0, line.unitBase - codeUnit)),
      };
    });
    return { lines: out, codeAmount: round2(codeAmount), applied };
  }

  const matching = decorated.filter((line) => line.matches);
  const sumBase = matching.reduce((sum, line) => sum + line.unitBase * line.quantity, 0);
  const sumAuto = matching.reduce(
    (sum, line) => sum + line.automaticUnitAmount * line.quantity,
    0
  );
  const fixedAmount = applyDiscountToAmount(sumBase, codeDiscount).amount;
  if (fixedAmount <= sumAuto + 0.001 || fixedAmount <= 0) {
    return { lines: decorated.map(automaticLine), codeAmount: 0, applied: false };
  }

  const shares = new Map();
  let allocated = 0;
  matching.forEach((line, index) => {
    const lineBase = line.unitBase * line.quantity;
    const share =
      index === matching.length - 1
        ? round2(fixedAmount - allocated)
        : round2(sumBase > 0 ? fixedAmount * (lineBase / sumBase) : 0);
    if (index !== matching.length - 1) allocated = round2(allocated + share);
    shares.set(line.key, share);
  });

  return {
    lines: decorated.map((line) => {
      const share = shares.get(line.key);
      if (share == null) return automaticLine(line);
      const unitAmount = line.quantity > 0 ? round2(share / line.quantity) : share;
      return {
        key: line.key,
        source: 'code',
        unitAmount,
        lineAmount: share,
        unitTotal: round2(Math.max(0, line.unitBase - unitAmount)),
      };
    }),
    codeAmount: fixedAmount,
    applied: true,
  };
};

const unitBaseOf = (item) => {
  const pricing = item?.pricing || {};
  const automatic = Math.max(0, Number(pricing.catalogDiscountAmount) || 0);
  const stored = Number(pricing.originalTotalPrice);
  if (Number.isFinite(stored) && stored > 0) return stored;
  return Math.max(0, (Number(pricing.totalPrice) || 0) + automatic);
};

/**
 * Return cart items with the redeemed code applied where it beats the
 * automatic discount. Items that stay on the automatic discount are unchanged.
 */
export const applyDiscountCodeToCartItems = (items, codeDiscount) => {
  if (!codeDiscount || !Array.isArray(items) || items.length === 0) return items || [];

  const allocation = allocateDiscountCode(
    items.map((item) => ({
      key: item.id,
      product: item.product || { id: item.productId },
      unitBase: unitBaseOf(item),
      quantity: item.quantity || 1,
      automaticUnitAmount: Math.max(0, Number(item.pricing?.catalogDiscountAmount) || 0),
    })),
    codeDiscount
  );
  if (!allocation.applied) return items;

  const byKey = new Map(allocation.lines.map((line) => [line.key, line]));
  return items.map((item) => {
    const alloc = byKey.get(item.id);
    if (!alloc || alloc.source !== 'code') return item;

    const pricing = item.pricing || {};
    const unitBase = unitBaseOf(item);
    const fromOriginal = unitBase > 0 ? alloc.unitTotal / unitBase : 1;
    const upsells = Array.isArray(item.upsells)
      ? item.upsells.map((upsell) => ({
          ...upsell,
          unitPrice: round2((Number(upsell.unitPrice) || 0) * fromOriginal),
          lineTotal: round2((Number(upsell.lineTotal) || 0) * fromOriginal),
        }))
      : item.upsells;
    const upsellSum = Array.isArray(upsells)
      ? round2(upsells.reduce((sum, upsell) => sum + (Number(upsell.lineTotal) || 0), 0))
      : round2((Number(pricing.upsellsPrice) || 0) * fromOriginal);
    const snapshot = buildCatalogDiscountSnapshot(
      { ...codeDiscount, method: 'code' },
      alloc.unitAmount
    );

    return {
      ...item,
      upsells,
      pricing: {
        ...pricing,
        totalPrice: alloc.unitTotal,
        productOnlyPrice: round2(Math.max(0, alloc.unitTotal - upsellSum)),
        upsellsPrice: upsellSum,
        catalogDiscountAmount: alloc.unitAmount,
        catalogDiscount: snapshot,
      },
    };
  });
};
